import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, inject, input, model, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { MenuModule } from 'primeng/menu';
import { PanelModule } from 'primeng/panel';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { TableRowSelectEvent } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { lastValueFrom } from 'rxjs';
import { GenericEnumDropDownComponent } from '../../../../shared/controls/generic-enum-drop-down/generic-enum-drop-down.component';
import { Document, DocumentReportHistory, ReportHistoryEventType, ReportHistorySearchInput, UserEMail } from '../../../../generated/generated-types';
import { BaseListWrapperComponent } from '../../../../shared/components/base-list-wrapper/base-list-wrapper.component';
import { NewBaseListComponent } from '../../../../shared/components/new-base-list-component/new-base-list-component';
import { PhxAvatarComponent } from '../../../../shared/components/phx-avatar/phx-avatar.component';
import { AuthService } from '../../../../shared/services/auth.service';
import { EmailUserService } from '../../../../shared/services/email-user.service';
import { DocumentReportHistoryService } from '../../../../shared/services/report-history.service';
import { AssetService } from '../../../asset/services/asset.service';
import { DocumentService } from '../../../documents/services/document.service';
import { ReportPrintListService } from '../../../printing/services/report-print.service';
import { ReportService } from '../../../printing/services/report.service';
import { AssetsPanelViewComponent } from '../assets-panel-view/assetsl-panel-view.component';
import { EmailPanelViewComponent } from '../email-panel-view/email-panel-view.component';
import { IEventData } from '../pop-over-panel-widget/interfaces-types';
import { PopOverPanelWidgetComponent } from '../pop-over-panel-widget/pop-over-panel-widget.component';
@Component({
  selector: 'app-report-history-list',
  templateUrl: './report-history-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    GenericEnumDropDownComponent,
    BaseListWrapperComponent,
    FormsModule,
    PopOverPanelWidgetComponent,
    TranslatePipe,
    PhxAvatarComponent,
    TooltipModule,
    PanelModule,
    ScrollPanelModule,
    MenuModule,
    EmailPanelViewComponent,
    AssetsPanelViewComponent
  ]
})
export class ReportHistoryListComponent extends NewBaseListComponent<DocumentReportHistory> implements AfterViewInit {

  protected documentService = inject(DocumentService);
  protected assetService = inject(AssetService);
  protected reportHistoryService = inject(DocumentReportHistoryService);
  protected reportPrintListService = inject(ReportPrintListService);
  protected reportService = inject(ReportService);
  protected authServices = inject(AuthService);
  protected userEmailService = inject(EmailUserService);

  override noAddNew = true;
  override noDelete = true;
  override noBaseEdit = true;
  override noCopy = true;
  override noPrinter = true;
  override noExport = true;
  override noSearch = true;
  override noColFilter = true;

  document = model.required<Document>();

  /** If true, the document property dropdown will not be shown. */
  noDocumentPropertyFilterDropdown = input<boolean>(false);

  ReportHistoryEventType = ReportHistoryEventType;

  // Only sHow the following document properties to be selected and filtered
  documentPropertiesWhiteListFilter: ReportHistoryEventType[] = [
    ReportHistoryEventType.PRINTED,
    ReportHistoryEventType.EMAILED,
    ReportHistoryEventType.DOWNLOADED,
    ReportHistoryEventType.SHOWEDPREVIEW,
    ReportHistoryEventType.ASSETS,
    ReportHistoryEventType.ASSET_AUTO_GENERATED,
    ReportHistoryEventType.ASSET_DELETED
  ];

  selectedDocumentProperties: ReportHistoryEventType[] = [];

  eventData = signal<IEventData<'userEmail' | 'assetIds', ReportHistoryEventType> | null>(null);

  /** Output the event that triggered the popover, mainly used to open the popover panel widget */
  event = signal<Event>(null);

  constructor() {
    super("DocumentReportHistory", inject(DocumentReportHistoryService));

    this.onClickIncludedEvent.subscribe(async (event: TableRowSelectEvent<any>) => {
      try {
        if (event) {
          switch (event.data.reportHistoryEvent) {
            case ReportHistoryEventType.EMAILED:
              this.applyEmailEventHandler(event);
              break;
            case ReportHistoryEventType.ASSETS:
              this.applyAssetsEventHandler(event);
              break;
            case ReportHistoryEventType.ASSET_AUTO_GENERATED:
              this.applyAssetAutoGeneratedEventHandler(event);
              break;
          }
        }
      } catch (error: any) {
        console.error("Error applying event handler", error);
      }
    });
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();

    this.onRefresh.subscribe((state: boolean) => {
      if (state) {
        this.eventData.set(null);
      }
    });

    this.reportHistoryService.reportHistorySaved$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((history) => {
      if (this.initialFetchExecuted && history?.documentId === this.document()?.id) {
        this.refresh();
      }
    });
  }

  async applyEmailEventHandler(event: TableRowSelectEvent<any>): Promise<void> {
    if (!event.data?.emailSentHistoryId) {
      // console.warn("Email Sent History Id is not defined");
      // console.warn("No Email History was saved for this document on that period of time. Try other period of time.");
      return;
    }
    // Before the big refactor we used to use the document link model, but i think since we now have all necessary information from the context.
    // I think we could only get the user email data from the emailSentHistoryId. Let me know if you think otherwise.
    let userEmailData: UserEMail | null = await lastValueFrom(
      this.userEmailService.getItem(event.data.emailSentHistoryId).single$)
      .then(x => x.getEmailSent)
      .catch(err => { console.error("Error Getting User Email", err); return null; });

    if (!userEmailData) {
      // console.warn("User Email is not defined, so makes impossible to get the user email from.");
      // console.warn("No User Email was found for this report history. Try other report history.");
      return;
    }

    this.eventData.set({
      eventType: event.data.reportHistoryEvent,
      data: userEmailData
    });
    this.event.set(event.originalEvent);
  }
  async applyAssetsEventHandler(event: TableRowSelectEvent<any>): Promise<void> {
    if (!event.data.assetId) {
      // console.warn("Asset Id is not defined, so makes impossible to get the asset from.");
      // console.warn("No Asset was found for this report history. Try other report history.");
      return;
    }
    this.eventData.set({
      eventType: event.data.reportHistoryEvent,
      data: [event.data.assetId]
    });
    this.event.set(event.originalEvent);
  }
  async applyAssetAutoGeneratedEventHandler(event: TableRowSelectEvent<any>): Promise<void> {
    // For now we dont have an specific handler for this event, so we use the same as the assets event.
    this.applyAssetsEventHandler(event);
  }

  /**
   * @description It refreshes the list of report history and set the selected document property, where will be used by getSearchInputForQuery method, later on.
   * @param val ReportHistoryEventType | ReportHistoryEventType[]
   * @returns void
   */
  selectedDocumentPropertyChanged(newStatus: ReportHistoryEventType[]): void {

    //check if array contents are different
    if (JSON.stringify(newStatus) !== JSON.stringify(this.selectedDocumentProperties)) {
      this.selectedDocumentProperties = newStatus;
      this.refresh();
    }
  }

  override getSearchInputForQuery(): ReportHistorySearchInput {

    let si: ReportHistorySearchInput = super.getSearchInputForQuery();
    si.documentId = this.document()?.id;
    si.reportHistoryEvent = this.selectedDocumentProperties.map((dp) => ReportHistoryEventType[dp]); // To convert the string back to the enum value of ReportHistoryEventType!!
    return si;
  }

  /** Unscoped API results when documentId is missing — never fetch without an id. */
  override async refresh(force: boolean = false): Promise<void> {
    if (!this.document()?.id) {
      return;
    }
    await super.refresh(force);
  }

  // Disable double click event - At the moment we don't have a route to navigate to YET
  override onDoubleClickRow(rowData: DocumentReportHistory, rowIndex?: number) {
    return;
  }

  getIcon(reportHistoryEvent?: ReportHistoryEventType) {
    switch (reportHistoryEvent) {
      // case "SHIPPED":
      //   return "fa fa-truck";
      // case "PARTSHIPPED":
      //   return "fa fa-truck-arrow-right";
      // case "COPIED":
      //   return "fa fa-copy";
      // case "REFERENCED":
      //   return "fa fa-code-compare";
      case "PRINTED":
        return "fa fa-print";
      case "DOWNLOADED":
        return "fa fa-download";
      case "EMAILED":
        return "fa fa-envelope";
      case "SHOWEDPREVIEW":
        return "fa fa-eye";
      case "ASSETS":
        return "fa fa-paperclip";
      case "ASSET_AUTO_GENERATED":
        return "fa fa-refresh";
      case "EINVOICE_EXPORTED":
        return "fa fa-file-code";
      // case "USER":
      //   return "fa fa-user";
    }

  }

}
